import { useEffect, useState } from 'react';
import { useI18n } from '../i18n/useI18n';
import {
  LoginRequiredError,
  Playlist,
  fetchAvailablePlaylists,
  fetchMaxRoomPlaylists,
  fetchRoomPlaylists,
  linkPlaylistToRoom,
  unlinkPlaylistFromRoom,
} from '../utils/roomPlaylistsApi';

interface Props {
  roomId: string;
  onClose: () => void;
}

export default function RoomPlaylistsModal({ roomId, onClose }: Props) {
  const { t } = useI18n();
  const [linked, setLinked] = useState<Playlist[]>([]);
  const [available, setAvailable] = useState<Playlist[]>([]);
  const [max, setMax] = useState(5);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);

  // /api/playlists só traz item_count; /api/rooms/:id/playlists traz items.
  const songCount = (p: Playlist) => p.items?.length ?? p.item_count ?? 0;

  const errorMessage = (err: unknown): string =>
    err instanceof LoginRequiredError
      ? t('room.playlists.loginRequired')
      : (err as any)?.message || t('room.playlists.error');

  const reload = async () => {
    const [roomPlaylists, allPlaylists, maxPlaylists] = await Promise.all([
      fetchRoomPlaylists(roomId),
      fetchAvailablePlaylists(),
      fetchMaxRoomPlaylists(),
    ]);
    setLinked(roomPlaylists);
    setAvailable(allPlaylists);
    setMax(maxPlaylists);
  };

  useEffect(() => {
    setLoaded(false);
    setError(null);
    reload().catch(() => setError(t('room.playlists.error'))).finally(() => setLoaded(true));
  }, [roomId]);

  const linkedIds = new Set(linked.map((p) => p.id));
  const candidates = available.filter((p) => !linkedIds.has(p.id));
  const atLimit = linked.length >= max;

  const handleAdd = async (playlistId: string) => {
    setBusyId(playlistId);
    setError(null);
    try {
      await linkPlaylistToRoom(roomId, playlistId);
      await reload();
    } catch (err: any) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (playlistId: string) => {
    setBusyId(playlistId);
    setError(null);
    try {
      await unlinkPlaylistFromRoom(roomId, playlistId);
      await reload();
    } catch (err: any) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card room-playlists-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{t('room.playlists.title')}</h3>
          <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <p className="room-playlists-hint">{t('room.playlists.hint')}</p>
          <div className="room-playlists-counter">
            {t('room.playlists.counter', { count: linked.length, max })}
          </div>

          {!loaded ? (
            <p className="stage-prefs-loading">{t('common.loading')}</p>
          ) : (
            <ul className="room-playlists-list">
              {linked.length === 0 && (
                <li className="room-playlists-empty">{t('room.playlists.empty')}</li>
              )}
              {linked.map((p) => (
                <li key={p.id} className="room-playlists-item">
                  <span className="room-playlists-item-name">{p.name}</span>
                  <span className="room-playlists-item-count">
                    {t('room.playlists.songCount', { count: songCount(p) })}
                  </span>
                  <button
                    className="room-playlists-remove"
                    onClick={() => handleRemove(p.id)}
                    disabled={busyId === p.id}
                    aria-label={t('room.playlists.remove')}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          {error && <p className="stage-prefs-error">{error}</p>}

          {selectorOpen && candidates.length > 0 && (
            <ul className="room-playlists-list room-playlists-list--selector">
              {candidates.map((p) => (
                <li key={p.id} className="room-playlists-item">
                  <span className="room-playlists-item-name">{p.name}</span>
                  <span className="room-playlists-item-count">
                    {t('room.playlists.songCount', { count: songCount(p) })}
                  </span>
                  <button
                    className="room-playlists-add"
                    onClick={() => handleAdd(p.id)}
                    disabled={busyId === p.id || atLimit}
                  >
                    {t('room.playlists.add')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            className="btn-secondary room-playlists-toggle-add"
            onClick={() => setSelectorOpen((v) => !v)}
            disabled={!loaded || (candidates.length === 0 && !selectorOpen)}
          >
            {selectorOpen ? t('room.playlists.hideAvailable') : t('room.playlists.showAvailable')}
          </button>
        </div>
      </div>
    </div>
  );
}
